import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Images, FileText, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ProductForm from "@/components/admin/ProductForm";
import { getBrand, getCategory, formatPrice, displayPrice } from "@/data/catalog";
import { adminProducts, adminDeleteProduct, adminBulkUpdate } from "@/lib/api";

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [selected, setSelected] = useState([]); // ids
  const [copyOpen, setCopyOpen] = useState(false);
  const [sourceId, setSourceId] = useState("");
  const [descOpen, setDescOpen] = useState(false);
  const [applyDesc, setApplyDesc] = useState(true);
  const [applyShort, setApplyShort] = useState(false);
  const [applyFeatures, setApplyFeatures] = useState(false);
  const [descVal, setDescVal] = useState("");
  const [shortVal, setShortVal] = useState("");
  const [featuresVal, setFeaturesVal] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    adminProducts().then(setProducts).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return products;
    return products.filter((p) => (p.name + " " + p.model + " " + p.brandSlug).toLowerCase().includes(s));
  }, [products, q]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const allFilteredSelected = filtered.length > 0 && filtered.every((p) => selectedSet.has(p.id));
  const toggleOne = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleAll = () => setSelected(allFilteredSelected ? selected.filter((id) => !filtered.some((p) => p.id === id)) : Array.from(new Set([...selected, ...filtered.map((p) => p.id)])));
  const clearSelection = () => setSelected([]);

  const remove = async (p) => {
    if (!window.confirm(`Deactivate "${p.name}"? (It will be hidden from the store.)`)) return;
    await adminDeleteProduct(p.id, false);
    toast.success("Product deactivated");
    load();
  };

  const sourceProduct = products.find((p) => p.id === sourceId);
  const copyTargets = selected.filter((id) => id !== sourceId); // never change the source

  const doCopyImages = async () => {
    if (!sourceProduct) return;
    const imgs = sourceProduct.images || [];
    setBusy(true);
    try {
      const res = await adminBulkUpdate(copyTargets, { images: imgs });
      toast.success(`Images copied to ${res.modified} product(s)`);
      setCopyOpen(false);
      setSourceId("");
      clearSelection();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk copy failed");
    } finally {
      setBusy(false);
    }
  };

  const doApplyDesc = async () => {
    const updates = {};
    if (applyDesc) updates.description = descVal;
    if (applyShort) updates.shortDescription = shortVal;
    if (applyFeatures) updates.features = featuresVal.split("\n").map((s) => s.trim()).filter(Boolean);
    if (Object.keys(updates).length === 0) {
      toast.error("Select at least one field to apply");
      return;
    }
    setBusy(true);
    try {
      const res = await adminBulkUpdate(selected, updates);
      toast.success(`Updated ${res.modified} product(s): ${res.fields.join(", ")}`);
      setDescOpen(false);
      clearSelection();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, model, brand…" className="max-w-xs rounded-sm" data-testid="admin-product-search" />
        <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="rounded-sm bg-sky-700 hover:bg-sky-800" data-testid="admin-add-product"><Plus className="h-4 w-4" /> Add Product</Button>
      </div>

      {/* Bulk action toolbar */}
      {selected.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3" data-testid="bulk-toolbar">
          <span className="font-mono text-sm font-bold text-sky-900" data-testid="bulk-selected-count">{selected.length} selected</span>
          <Button size="sm" variant="outline" className="rounded-sm border-slate-300" onClick={() => { setSourceId(""); setCopyOpen(true); }} data-testid="bulk-copy-images"><Images className="h-4 w-4" /> Copy Images From Product</Button>
          <Button size="sm" variant="outline" className="rounded-sm border-slate-300" onClick={() => setDescOpen(true)} data-testid="bulk-apply-desc"><FileText className="h-4 w-4" /> Apply Description / Features</Button>
          <Button size="sm" variant="ghost" className="ml-auto rounded-sm text-slate-600" onClick={clearSelection} data-testid="bulk-clear"><X className="h-4 w-4" /> Clear selection</Button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3"><Checkbox checked={allFilteredSelected} onCheckedChange={toggleAll} data-testid="bulk-select-all" aria-label="Select all" /></th>
                <th className="px-4 py-3">Product</th>
                <th className="px-4 py-3">Brand</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Price</th>
                <th className="px-4 py-3">Flags</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((p) => {
                const dp = displayPrice(p);
                return (
                  <tr key={p.id} className={`${p.active ? "" : "opacity-50"} ${selectedSet.has(p.id) ? "bg-sky-50/60" : ""}`} data-testid={`admin-row-${p.slug}`}>
                    <td className="px-4 py-3"><Checkbox checked={selectedSet.has(p.id)} onCheckedChange={() => toggleOne(p.id)} data-testid={`bulk-select-${p.slug}`} aria-label={`Select ${p.name}`} /></td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900">{p.name}</div>
                      <div className="font-mono text-xs text-slate-500">{p.model}{p.images?.length ? ` · ${p.images.length} img` : ""}</div>
                    </td>
                    <td className="px-4 py-3">{getBrand(p.brandSlug)?.name}</td>
                    <td className="px-4 py-3 text-slate-600">{getCategory(p.categorySlug)?.name || p.categorySlug}</td>
                    <td className="px-4 py-3">{dp.mode === "show" ? formatPrice(dp.price) : <span className="text-slate-400">Request</span>}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {p.featured && <Badge variant="secondary" className="rounded-sm text-[10px]">Featured</Badge>}
                        {p.clearance && <Badge className="rounded-sm bg-orange-500 text-[10px] hover:bg-orange-500">Clearance</Badge>}
                        {!p.active && <Badge variant="outline" className="rounded-sm text-[10px]">Inactive</Badge>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <Button size="icon" variant="ghost" onClick={() => { setEditing(p); setFormOpen(true); }} data-testid={`admin-edit-${p.slug}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => remove(p)} className="text-red-600 hover:text-red-700" data-testid={`admin-delete-${p.slug}`}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ProductForm open={formOpen} onClose={() => setFormOpen(false)} product={editing} onSaved={load} />

      {/* Copy Images dialog */}
      <Dialog open={copyOpen} onOpenChange={setCopyOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Copy Images From Product</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Choose a source product. Its image references will <strong>replace</strong> the images of the <strong data-testid="copy-target-count">{copyTargets.length}</strong> selected target product(s). The source product is never changed, and no files are re-uploaded.</p>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Image source product</Label>
              <Select value={sourceId} onValueChange={setSourceId}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="copy-source-select"><SelectValue placeholder="Select a product…" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {products.filter((p) => (p.images || []).length > 0).sort((a, b) => a.name.localeCompare(b.name)).map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name} ({p.images.length} img)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {products.filter((p) => (p.images || []).length > 0).length === 0 && (
                <p className="mt-2 text-xs text-amber-600">No products currently have images to copy from.</p>
              )}
            </div>
            {sourceProduct && (
              <div className="rounded-sm border border-slate-200 bg-slate-50 p-3 text-sm">
                Source: <strong>{sourceProduct.name}</strong> — {(sourceProduct.images || []).length} image(s). Targets: {copyTargets.length}.
                {selected.includes(sourceId) && <span className="block text-xs text-slate-500">(The source is in your selection and will be skipped as a target.)</span>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setCopyOpen(false)}>Cancel</Button>
            <Button className="rounded-sm bg-sky-700 hover:bg-sky-800" disabled={!sourceProduct || copyTargets.length === 0 || busy} onClick={doCopyImages} data-testid="copy-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Replace images on {copyTargets.length} product(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apply Description / Features dialog */}
      <Dialog open={descOpen} onOpenChange={setDescOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Apply Description / Features</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Only the checked fields will be overwritten on the <strong>{selected.length}</strong> selected product(s). All other data (model, title, specs, prices, categories, images) is preserved.</p>

            <div className="space-y-2 rounded-sm border border-slate-200 p-3">
              <label className="flex items-center gap-2 text-sm font-medium"><Checkbox checked={applyDesc} onCheckedChange={(v) => setApplyDesc(!!v)} data-testid="apply-desc-check" /> Full description</label>
              {applyDesc && <Textarea value={descVal} onChange={(e) => setDescVal(e.target.value)} rows={4} placeholder="Full description to apply…" className="rounded-sm" data-testid="apply-desc-text" />}
            </div>
            <div className="space-y-2 rounded-sm border border-slate-200 p-3">
              <label className="flex items-center gap-2 text-sm font-medium"><Checkbox checked={applyShort} onCheckedChange={(v) => setApplyShort(!!v)} data-testid="apply-short-check" /> Short description</label>
              {applyShort && <Textarea value={shortVal} onChange={(e) => setShortVal(e.target.value)} rows={2} placeholder="Short description to apply…" className="rounded-sm" data-testid="apply-short-text" />}
            </div>
            <div className="space-y-2 rounded-sm border border-slate-200 p-3">
              <label className="flex items-center gap-2 text-sm font-medium"><Checkbox checked={applyFeatures} onCheckedChange={(v) => setApplyFeatures(!!v)} data-testid="apply-features-check" /> Key features (one per line)</label>
              {applyFeatures && <Textarea value={featuresVal} onChange={(e) => setFeaturesVal(e.target.value)} rows={4} placeholder={"Feature one\nFeature two"} className="rounded-sm" data-testid="apply-features-text" />}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setDescOpen(false)}>Cancel</Button>
            <Button className="rounded-sm bg-sky-700 hover:bg-sky-800" disabled={busy} onClick={doApplyDesc} data-testid="apply-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Apply to {selected.length} product(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
